import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Sparkles, Check, SkipForward, ArrowLeft, ArrowRight, Wand2, Bookmark } from 'lucide-react';
import { aiParseInstruction, updateBankTransaction, createTransactionRule } from '@/lib/bankTransactionsApi';
import { toast } from 'sonner';

import { useAISuggestions } from './aiReview/useAISuggestions';
import { TransactionReviewCard } from './aiReview/TransactionReviewCard';
import { CategoryPicker } from './aiReview/CategoryPicker';

export default function AIReviewDialog({ open, onOpenChange, transactions, categories, onComplete }) {
  const { suggestions, loadedCount } = useAISuggestions(open, transactions);
  const [applied, setApplied] = useState({});
  const [index, setIndex] = useState(0);
  const [edits, setEdits] = useState({});
  const [instruction, setInstruction] = useState('');
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rememberRule, setRememberRule] = useState(true);

  // Reset state when dialog opens with a new batch
  useEffect(() => {
    if (open) {
      setApplied({}); setEdits({}); setIndex(0); setInstruction('');
    }
  }, [open, transactions]);

  const current = suggestions[index];
  const currentEdit = current
    ? (edits[current.id] || { category: current.category, subcategory: current.subcategory, detail: current.detail })
    : null;

  const updateEdit = (patch) => {
    if (!current) return;
    setEdits(e => ({
      ...e,
      [current.id]: { ...(e[current.id] || { category: current.category, subcategory: current.subcategory, detail: current.detail }), ...patch },
    }));
  };

  const goNext = () => setIndex(i => Math.min(i + 1, suggestions.length - 1));
  const goPrev = () => setIndex(i => Math.max(i - 1, 0));

  const handleAccept = async () => {
    if (!current || !currentEdit?.category) {
      toast.error('Select a category before accepting');
      return;
    }
    setSaving(true);
    try {
      await updateBankTransaction(current.id, {
        category: currentEdit.category,
        subcategory: currentEdit.subcategory || '',
        detail: currentEdit.detail || '',
      });
      if (rememberRule && current.txn?.description) {
        createTransactionRule({
          sample_description: current.txn.description,
          category: currentEdit.category,
          subcategory: currentEdit.subcategory || null,
          detail: currentEdit.detail || null,
          source: 'auto',
        }).catch(() => { /* silent */ });
      }
      setApplied(a => ({ ...a, [current.id]: true }));
      toast.success(rememberRule ? 'Categorized & rule learned' : 'Categorized');
      if (index < suggestions.length - 1) { goNext(); setInstruction(''); }
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    if (index < suggestions.length - 1) { goNext(); setInstruction(''); }
  };

  // Keep latest handlers in ref for keyboard shortcuts
  const handlersRef = useRef({});
  useEffect(() => {
    handlersRef.current = { handleAccept, goPrev, goNext, handleSkip, index, suggestionsLen: suggestions.length };
  });

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      const t = e.target;
      const tag = t?.tagName?.toLowerCase();
      const isTyping = tag === 'input' || tag === 'textarea' || tag === 'select' || t?.isContentEditable;
      if (isTyping) return;
      const h = handlersRef.current;
      if (e.key === 'ArrowRight' && h.index < h.suggestionsLen - 1) { e.preventDefault(); h.goNext(); }
      else if (e.key === 'ArrowLeft' && h.index > 0) { e.preventDefault(); h.goPrev(); }
      else if (e.key === 'Enter') { e.preventDefault(); h.handleAccept(); }
      else if (e.key.toLowerCase() === 's') { e.preventDefault(); h.handleSkip(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const handleParseInstruction = async () => {
    if (!current || !instruction.trim()) return;
    setParsing(true);
    try {
      const parsed = await aiParseInstruction(current.id, instruction.trim());
      if (parsed.category) {
        updateEdit({ category: parsed.category, subcategory: parsed.subcategory || '', detail: parsed.detail || '' });
        toast.success('Parsed — review and click Accept');
      } else {
        toast.error(parsed.error || 'Could not parse instruction');
      }
    } catch {
      toast.error('Parse failed');
    } finally {
      setParsing(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    const appliedCount = Object.keys(applied).length;
    if (appliedCount > 0 && onComplete) onComplete(appliedCount);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ai-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            AI Categorization Review
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2 flex-wrap">
            <span>Review each suggestion — accept, override with plain language, or skip</span>
            <span className="text-[10px] text-muted-foreground/80" data-testid="review-shortcut-hint">
              <kbd className="px-1 py-0 rounded border border-border/60 bg-[#faf8f4] text-[9px]">←</kbd>
              <kbd className="ml-0.5 px-1 py-0 rounded border border-border/60 bg-[#faf8f4] text-[9px]">→</kbd>
              <span className="ml-1">nav</span>
              <span className="mx-1">·</span>
              <kbd className="px-1 py-0 rounded border border-border/60 bg-[#faf8f4] text-[9px]">↵</kbd>
              <span className="ml-1">accept</span>
              <span className="mx-1">·</span>
              <kbd className="px-1 py-0 rounded border border-border/60 bg-[#faf8f4] text-[9px]">S</kbd>
              <span className="ml-1">skip</span>
            </span>
          </DialogDescription>
        </DialogHeader>

        {suggestions.length === 0 && (
          <div className="py-16 text-center text-sm text-muted-foreground">No suggestions available.</div>
        )}

        {current && (
          <div className="space-y-4 py-1">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground" data-testid="ai-review-progress">
                Transaction {index + 1} of {suggestions.length}
                {applied[current.id] && <Badge variant="outline" className="ml-2 text-[10px] border-emerald-300 text-emerald-700 bg-emerald-50"><Check className="h-2.5 w-2.5 mr-0.5" />Applied</Badge>}
              </span>
              <span className="text-xs text-muted-foreground flex items-center gap-2">
                {loadedCount < suggestions.length && (
                  <span className="inline-flex items-center gap-1 text-primary" data-testid="ai-loading-progress">
                    <Sparkles className="h-3 w-3 animate-pulse" />
                    Loading AI… {loadedCount}/{suggestions.length}
                  </span>
                )}
                <span>{Object.keys(applied).length} applied</span>
              </span>
            </div>
            <div className="h-1.5 bg-[#f0ece6] rounded-full overflow-hidden">
              <div className="h-full bg-primary transition-all" style={{ width: `${((index + 1) / suggestions.length) * 100}%` }} />
            </div>

            <TransactionReviewCard current={current} />

            <CategoryPicker categories={categories} value={currentEdit} onChange={updateEdit} />

            <div className="space-y-1.5">
              <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                <Wand2 className="h-3 w-3" /> Not happy? Tell the AI where it should go
              </Label>
              <div className="flex gap-2">
                <Input
                  value={instruction}
                  onChange={e => setInstruction(e.target.value)}
                  placeholder="e.g., move this to rent income, zelle"
                  className="text-xs"
                  data-testid="review-instruction-input"
                  onKeyDown={e => { if (e.key === 'Enter' && !parsing) { e.preventDefault(); handleParseInstruction(); } }}
                />
                <Button variant="outline" size="sm" onClick={handleParseInstruction} disabled={!instruction.trim() || parsing} data-testid="review-parse-btn">
                  {parsing ? 'Parsing...' : 'Apply Instruction'}
                </Button>
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none pt-1" data-testid="remember-rule-row">
              <input
                type="checkbox"
                checked={rememberRule}
                onChange={e => setRememberRule(e.target.checked)}
                className="rounded border-gray-300"
                data-testid="remember-rule-checkbox"
              />
              <Bookmark className="h-3.5 w-3.5 text-primary" />
              <span className="text-[12px] text-[#5b6773]">
                <span className="font-medium">Remember this categorization</span>
                <span className="text-muted-foreground ml-1">— auto-apply to similar transactions in future imports</span>
              </span>
            </label>
          </div>
        )}

        <DialogFooter className="flex flex-wrap gap-2">
          {current && (
            <>
              <Button variant="outline" size="sm" onClick={goPrev} disabled={index === 0} data-testid="review-prev-btn">
                <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Prev
              </Button>
              <Button variant="outline" size="sm" onClick={handleSkip} disabled={index >= suggestions.length - 1} data-testid="review-skip-btn">
                <SkipForward className="h-3.5 w-3.5 mr-1" /> Skip
              </Button>
              <Button size="sm" onClick={handleAccept} disabled={saving || !currentEdit?.category} data-testid="review-accept-btn">
                <Check className="h-3.5 w-3.5 mr-1" />
                {saving ? 'Saving...' : (index < suggestions.length - 1 ? 'Accept & Next' : 'Accept')}
              </Button>
              <Button variant="outline" size="sm" onClick={goNext} disabled={index >= suggestions.length - 1} data-testid="review-next-btn">
                Next <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
              <div className="flex-1" />
              <Button variant="ghost" size="sm" onClick={handleClose} data-testid="review-close-btn">Done</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
